<template>
  <div v-if="aml">
    <ui-heading tag="h3" no-top-margin>
      {{ $t("aml_screening_widget.heading") }}
    </ui-heading>
    <div class="status-row">
      <span class="status-label" :class="{ 'missing-data': aml.missingData }">
        <icon name="check-circle" v-if="aml.success" class="icon success" />
        <icon
          name="alert-circle"
          v-else
          class="icon"
          :class="{ unavailable: aml.unavailable, fail: aml.fail }"
        />
        {{ aml.statusText }}
      </span>
      <ui-button
        v-if="!aml.unavailable"
        variant="primary"
        size="sm"
        class="action-button"
        @click="openModal"
      >
        {{ $t("aml_screening_widget.view_button") }}
      </ui-button>
    </div>
    <ui-dialog
      :open="isModalOpen"
      bare
      width="53rem"
      @update:open="isModalOpen = $event"
    >
      <arbitrium-react-island
        :identification-id="identificationId"
        :person-name="personName"
        :person-date-of-birth="personDateOfBirth"
        :person-gender="personGender"
        :person-country="personCountry"
        :get-auth-token="getAuthToken"
        :locale="$i18n.locale"
        :open="isModalOpen"
        @close="closeModal"
        @status-change="handleStatusChange"
      />
    </ui-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { actions, getters } from "@/store";
import { CHECK_REASON, CHECK_STATUS } from "@/router/constants";
import { getReasonsLabels } from "@/utils/checks";
import ArbitriumReactIsland from "@/components/ArbitriumReactIsland.vue";

export default {
  components: { ArbitriumReactIsland },
  data() {
    return {
      isModalOpen: false,
      hasUnsyncedChanges: false
    };
  },
  watch: {
    isModalOpen(open) {
      if (open || !this.hasUnsyncedChanges) {
        return;
      }
      this.hasUnsyncedChanges = false;
      this.getIdentification({ identificationId: this.identificationId });
    }
  },
  computed: {
    ...mapGetters([getters.currentIdentification, getters.currentDocumentData]),
    identificationId() {
      return this.currentIdentification?.identificationId;
    },
    personName() {
      return [
        this.currentDocumentData?.firstName,
        this.currentDocumentData?.lastName
      ]
        .filter(Boolean)
        .join(" ");
    },
    personDateOfBirth() {
      return this.currentDocumentData?.dateOfBirth ?? "";
    },
    personGender() {
      return this.currentDocumentData?.gender ?? "";
    },
    personCountry() {
      return this.currentDocumentData?.country ?? "";
    },
    amlRegistry() {
      return this.currentIdentification?.data?.registries?.data?.find(
        reg => reg.name === "AML_SCREENING"
      );
    },
    // Icon/text reflect only the backend AML_SCREENING rule status — no
    // client-side "resolved" state. The backend recomputes this status from the
    // match-review decisions, so once the modal closes after any resolution we
    // refetch the identification (see the isModalOpen watcher) and let the fresh
    // backend status drive the widget, rather than faking a green state.
    aml() {
      if (!this.amlRegistry) {
        return null;
      }

      const { status, reasons } = this.amlRegistry;

      return {
        success: status === CHECK_STATUS.SUCCESS,
        fail: status === CHECK_STATUS.FAIL,
        unavailable: status === CHECK_STATUS.UNAVAILABLE,
        missingData: !!reasons?.includes(
          CHECK_REASON.MISSING_REQUIRED_DOCUMENT_DATA
        ),
        statusText: this.getStatusText(status, reasons)
      };
    }
  },
  methods: {
    ...mapActions([actions.getIdentification]),
    getStatusText(status, reasons) {
      if (status === CHECK_STATUS.SUCCESS) {
        return this.$t("aml_screening_widget.status_success");
      }

      if (status === CHECK_STATUS.FAIL) {
        return this.$t("aml_screening_widget.status_fail");
      }

      const reasonLabels =
        getReasonsLabels(reasons, "checks_registries_reason", this.$tIf) ?? [];

      return [
        this.$t("aml_screening_widget.status_unavailable"),
        ...reasonLabels
      ].join(" - ");
    },
    openModal() {
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
    },
    // A method (not an inline template arrow) so the island receives a
    // stable function reference and doesn't re-render on every unrelated
    // parent re-render.
    getAuthToken() {
      return this.$store.getters.token;
    },
    handleStatusChange() {
      this.hasUnsyncedChanges = true;
    }
  }
};
</script>

<style scoped lang="scss">
.status-row {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
}
.status-label {
  display: flex;
  gap: 0.5rem;
  align-items: center;

  &.missing-data {
    color: var(--text-color-dimmed);

    .icon {
      fill: currentColor;
    }
  }
}
.icon {
  flex-shrink: 0;
  width: 1.2rem;
  height: 1.2rem;
  &.success {
    fill: var(--text-success);
  }
  &.unavailable {
    fill: var(--text-warning);
  }
  &.fail {
    fill: var(--text-danger);
  }
}
.action-button.action-button {
  display: inline-flex;
  flex-shrink: 0;
  gap: 0.25em;
  align-items: center;
  justify-content: center;
}
</style>
